import { motion } from 'framer-motion';
import { LayoutDashboard, Check, Network, Lightbulb, Target } from 'lucide-react';
import platformScreenshot from '@/assets/platform-studio-screenshot.png';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const platformData = {
  badge: {
    text: "Your AI Cockpit",
    variant: "default" as const
  },
  icon: LayoutDashboard,
  iconColor: "bg-primary",
  heading: "Best-in-class AI-agnostic and brand-centric platform",
  tagline: "The gimmefy platform unifies all leading AI models in a single, brand-trained platform that puts you in control.\nBring your marketing vision to life with streamlined research, ideation, creation, editing, and publishing for every market and format.",
  features: ["Built for marketing teams' demands, not generic AI", "Unified workspace from research to publish", "On-brand always with brand vaults and marketing-trained AI", "Research, ideation, copy, visuals and video"],
  buttonText: "Explore Platform",
  buttonLink: "/comparison",
  buttonVariant: "default" as const
};

const PlatformSection = () => {
  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }} 
          transition={{ duration: 0.5 }} 
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-6xl mx-auto"
        >
          {/* Left: Content */}
          <div className="text-center lg:text-left">
            <Badge variant={platformData.badge.variant} className="w-fit mb-4 mx-auto lg:mx-0">
              {platformData.badge.text}
            </Badge>
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 mb-4 justify-center lg:justify-start">
              <div className={`w-12 h-12 ${platformData.iconColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                <LayoutDashboard className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl lg:text-3xl font-bold text-foreground text-center sm:text-left">
                {platformData.heading}
              </h3>
            </div>
            <p className="mb-3 text-secondary-foreground text-base lg:text-lg">
              The gimmefy platform unifies all leading AI models in a single, brand-trained{" "}
              <span className="bg-gradient-primary bg-clip-text text-transparent font-semibold">
                platform that puts you in control.
              </span>
            </p>
            <p className="mb-6 text-secondary-foreground text-base lg:text-lg">
              Bring{" "}
              <span className="bg-gradient-primary bg-clip-text text-transparent font-semibold">
                your marketing vision
              </span>{" "}
              to life with streamlined research, ideation, creation, editing, and publishing for{" "}
              <span className="bg-gradient-primary bg-clip-text text-transparent font-semibold">
                every market and format.
              </span>
            </p>
            <div className="grid grid-cols-1 gap-2 mb-8">
              {platformData.features.map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.1 }}
                  className="flex items-start space-x-3 p-3 justify-center lg:justify-start"
                >
                  <Check className="h-5 w-5 flex-shrink-0 text-primary mt-0.5" />
                  <span className="text-foreground text-sm font-medium text-left">{feature}</span>
                </motion.div>
              ))}
            </div>
            <div className="flex justify-center lg:justify-start">
              <Button variant={platformData.buttonVariant} asChild>
                <Link to={platformData.buttonLink}>{platformData.buttonText}</Link>
              </Button>
            </div>
          </div>

          {/* Right: Product Screenshot with Orbiting Badges */}
          <div className="relative flex items-center justify-center py-8 lg:py-12">
            {/* Orbiting container - hidden on mobile for cleaner look */}
            <motion.div 
              animate={{ rotate: 360 }} 
              transition={{ duration: 30, repeat: Infinity, ease: "linear" }} 
              className="hidden lg:flex absolute inset-0 items-center justify-center z-20" 
              style={{ width: '100%', height: '100%' }}
            >
              {/* AI-Agnostic Badge - positioned at top (0deg) */}
              <motion.div 
                animate={{ rotate: -360 }} 
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }} 
                className="absolute z-10 flex flex-col items-center gap-2" 
                style={{ top: '0%', left: '50%', transform: 'translateX(-50%)' }}
              >
                <span className="text-sm font-semibold text-amber-600 whitespace-nowrap">AI-Agnostic</span>
                <div className="w-12 h-12 rounded-full bg-amber-400 flex items-center justify-center shadow-lg">
                  <Network className="h-6 w-6 text-white" />
                </div>
              </motion.div>

              {/* Marketing Smart Badge - positioned at bottom-left (210deg) */}
              <motion.div 
                animate={{ rotate: -360 }} 
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }} 
                className="absolute z-10 flex flex-col items-center gap-2" 
                style={{ bottom: '5%', left: '10%' }}
              >
                <div className="w-12 h-12 rounded-full bg-teal-400 flex items-center justify-center shadow-lg">
                  <Lightbulb className="h-6 w-6 text-white" />
                </div>
                <span className="text-sm font-semibold text-teal-600 whitespace-nowrap">Marketing Smart</span>
              </motion.div>

              {/* Brand Centric Badge - positioned at bottom-right (330deg) */}
              <motion.div 
                animate={{ rotate: -360 }} 
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }} 
                className="absolute z-10 flex flex-col items-center gap-2" 
                style={{ bottom: '5%', right: '10%' }}
              >
                <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center shadow-lg">
                  <Target className="h-6 w-6 text-white" />
                </div>
                <span className="text-sm font-semibold text-red-500 whitespace-nowrap">Brand Centric</span>
              </motion.div>
            </motion.div>

            {/* Central Product Screenshot with glow */}
            <div className="w-full max-w-xs sm:max-w-sm lg:max-w-md relative z-10">
              {/* Glow pulse behind screenshot */}
              <motion.div
                className="absolute -inset-4 bg-primary/10 rounded-3xl blur-2xl hidden lg:block"
                animate={{ scale: [0.95, 1.05, 0.95], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              />
              <img 
                src={platformScreenshot} 
                alt="gimmefy Studio interface showing AI-powered marketing workspace" 
                className="relative w-full h-auto rounded-2xl shadow-xl border border-border/50"
              />
              
              {/* Mobile: Static badges below image */}
              <div className="flex lg:hidden justify-center gap-4 mt-4">
                <div className="flex flex-col items-center gap-1">
                  <div className="w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center shadow-md">
                    <Network className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-xs font-semibold text-amber-600">AI-Agnostic</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <div className="w-10 h-10 rounded-full bg-teal-400 flex items-center justify-center shadow-md">
                    <Lightbulb className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-xs font-semibold text-teal-600">Marketing Smart</span>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center shadow-md">
                    <Target className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-xs font-semibold text-red-500">Brand Centric</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PlatformSection;
